/**
 * Structured logging utility for Chloropal
 * Captures logs with context and timestamps for debugging
 */

export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

export interface LogEntry {
  id: string;
  timestamp: Date;
  level: LogLevel;
  category: string;
  message: string;
  data?: unknown;
  error?: {
    name: string;
    message: string;
    stack?: string;
    code?: string;
  };
}

// In-memory log storage
const MAX_LOGS = 200;
let logs: LogEntry[] = [];
const listeners: Set<(logs: LogEntry[]) => void> = new Set();

function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function notifyListeners() {
  listeners.forEach((listener) => listener([...logs]));
}

function addLog(entry: LogEntry) {
  logs.push(entry);
  if (logs.length > MAX_LOGS) {
    logs = logs.slice(-MAX_LOGS);
  }
  notifyListeners();
}

function formatError(error: unknown): LogEntry['error'] | undefined {
  if (!error) return undefined;

  if (error instanceof Error) {
    return {
      name: error.name,
      message: error.message,
      stack: error.stack,
      code: (error as Error & { code?: string }).code,
    };
  }

  if (typeof error === 'object' && error !== null) {
    const errObj = error as Record<string, unknown>;
    return {
      name: String(errObj.name ?? 'Error'),
      message: String(errObj.message ?? JSON.stringify(error)),
      code: errObj.code ? String(errObj.code) : undefined,
    };
  }

  return {
    name: 'Error',
    message: String(error),
  };
}

/**
 * Main logger object
 */
export const logger = {
  debug(category: string, message: string, data?: unknown) {
    const entry: LogEntry = {
      id: generateId(),
      timestamp: new Date(),
      level: 'debug',
      category,
      message,
      data,
    };
    addLog(entry);
    console.debug(`[${category}]`, message, data ?? '');
  },

  info(category: string, message: string, data?: unknown) {
    const entry: LogEntry = {
      id: generateId(),
      timestamp: new Date(),
      level: 'info',
      category,
      message,
      data,
    };
    addLog(entry);
    console.info(`[${category}]`, message, data ?? '');
  },

  warn(category: string, message: string, data?: unknown) {
    const entry: LogEntry = {
      id: generateId(),
      timestamp: new Date(),
      level: 'warn',
      category,
      message,
      data,
    };
    addLog(entry);
    console.warn(`[${category}]`, message, data ?? '');
  },

  error(category: string, message: string, error?: unknown, data?: unknown) {
    const entry: LogEntry = {
      id: generateId(),
      timestamp: new Date(),
      level: 'error',
      category,
      message,
      data,
      error: formatError(error),
    };
    addLog(entry);
    console.error(`[${category}]`, message, error, data ?? '');
  },

  /**
   * Subscribe to log updates
   */
  subscribe(listener: (logs: LogEntry[]) => void): () => void {
    listeners.add(listener);
    // Immediately call with current logs
    listener([...logs]);
    return () => listeners.delete(listener);
  },

  /**
   * Get all current logs
   */
  getLogs(): LogEntry[] {
    return [...logs];
  },

  /**
   * Clear all logs
   */
  clear() {
    logs = [];
    notifyListeners();
  },

  /**
   * Export logs as JSON string for sharing/debugging
   */
  export(): string {
    return JSON.stringify(logs, null, 2);
  },

  /**
   * Export logs as downloadable blob
   */
  exportAsBlob(): Blob {
    const data = this.export();
    return new Blob([data], { type: 'application/json' });
  },
};

// Expose logger globally for console debugging
if (typeof window !== 'undefined') {
  (window as unknown as { chloropal: { logger: typeof logger } }).chloropal = { logger };
}
